import { Numeric } from "@rothoblaas/design-system";
import { NoticeFormField as FormField } from "../notices/NoticeFormField";
import classNames from "classnames";
import { FC } from "react";
import { Controller, useFormContext } from "react-hook-form";
import useI18n from "../../../../../hooks/useTranslations";
import { LC_IMG } from "../../constants";
import styles from "../../LockCalculator.module.css";
import type { LockCalculatorFormValues } from "../../model/form";
import { headerOrientationLabels } from "../../model/structuralLabels";
import { MaterialGradeField } from "./MaterialGradeField";

// ─── Label helpers ───

// const catalogItemLabel = (c: { code: string; name: string }) => c.name;
const orientationImages: Record<string, string> = { beam: `${LC_IMG}/beam.png`, column: `${LC_IMG}/column.png` };
// ─── Props ───

interface TimberFormProps {
  readOnly?: boolean;
  onOpenHelp?: (text: string, image?: string) => void;
}

// ─── Component ───

export const TimberForm: FC<TimberFormProps> = ({ readOnly = false, onOpenHelp }) => {
  const i18n = useI18n();
  const {
    control,
    formState: { errors },
  } = useFormContext<LockCalculatorFormValues>();
  const t = i18n.translation;

  return (
    <div className={styles.timberContainer}>
      <div className={styles.timberRow}>
        {/* ═══════ Header member ═══════ */}
        <div className={styles.timberColumn}>
          <div className={styles.compactFormStack}>
            <h4 style={{ margin: 0 }}>{t.lockCalculatorModule?.timber?.header}</h4>

            <MaterialGradeField
              name="headerMember.materialGradeId"
              label={t.lockCalculatorModule?.timber?.headerMaterialGrade}
              helpText={t.lockCalculatorModule?.helpTexts?.headerMaterialGrade}
              readOnly={readOnly}
              onOpenHelp={onOpenHelp}
            />

            <Controller
              name="headerMember.width"
              control={control}
              render={({ field }) => (
                <FormField name="headerMember.width"
                  label={t.lockCalculatorModule?.timber?.headerWidth}
                  error={errors.headerMember?.width}
                 
                  showHelp={{
                    tooltip: t.lockCalculatorModule?.helpTexts?.headerWidth,
                    onOpen: () => onOpenHelp?.(t.lockCalculatorModule?.helpTexts?.headerWidth),
                  }}
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      value={field.value}
                      decimalScale={1}
                      suffix={t.common.unitMm}
                      id={labelId}
                      isInvalid={isInvalid}
                      allowNegative={false}
                      disabled={readOnly}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />

            <Controller
              name="headerMember.height"
              control={control}
              render={({ field }) => (
                <FormField name="headerMember.height"
                  label={t.lockCalculatorModule?.timber?.headerHeight}
                  error={errors.headerMember?.height}
                  showHelp={{
                    tooltip: t.lockCalculatorModule?.helpTexts?.headerHeight,
                    onOpen: () => onOpenHelp?.(t.lockCalculatorModule?.helpTexts?.headerHeight),
                  }}
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      value={field.value}
                      decimalScale={1}
                      suffix={t.common.unitMm}
                      id={labelId}
                      isInvalid={isInvalid}
                      allowNegative={false}
                      disabled={readOnly}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />

            <Controller
              name="headerMember.orientation"
              control={control}
              render={({ field }) => (
                <FormField name="headerMember.orientation"
                  label={t.lockCalculatorModule?.structural?.headerOrientation}
                  error={errors.headerMember?.orientation}
                  showHelp={{
                    tooltip: t.lockCalculatorModule?.helpTexts?.headerOrientation,
                    onOpen: () => onOpenHelp?.(t.lockCalculatorModule?.helpTexts?.headerOrientation),
                  }}
                >
                  <div className={`${styles.imageSelectGrid} ${styles.imageSelectGrid2}`}>
                    {buildHeaderOrientationOptions(t).map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={classNames(styles.imageSelectOption, {
                          [styles.imageSelectOptionSelected]: field.value === option.value,
                        })}
                        aria-pressed={field.value === option.value}
                        disabled={readOnly}
                        title={option.label}
                        onClick={() => field.onChange(field.value === option.value ? null : option.value)}
                      >
                        <img
                          className={styles.imageSelectImage}
                          src={orientationImages[option.value]}
                          alt={option.label}
                        />
                        <span className={styles.imageSelectLabel}>{option.label}</span>
                      </button>
                    ))}
                  </div>
                </FormField>
              )}
            />
          </div>
        </div>

        {/* ═══════ Vertical divider ═══════ */}
        <div className={styles.timberDivider} />

        {/* ═══════ Joist member ═══════ */}
        <div className={styles.timberColumn}>
          <div className={styles.compactFormStack}>
            <h4 style={{ margin: 0 }}>{t.lockCalculatorModule?.timber?.joist}</h4>

            <MaterialGradeField
              name="joistMember.materialGradeId"
              label={t.lockCalculatorModule?.timber?.joistMaterialGrade}
              helpText={t.lockCalculatorModule?.helpTexts?.joistMaterialGrade}
              readOnly={readOnly}
              onOpenHelp={onOpenHelp}
            />

            <Controller
              name="joistMember.width"
              control={control}
              rules={{ min: 0 }}
              render={({ field }) => (
                <FormField name="joistMember.width"
                  label={t.lockCalculatorModule?.timber?.joistWidth}
                  error={errors.joistMember?.width}
                  showHelp={{
                    tooltip: t.lockCalculatorModule?.helpTexts?.joistWidth,
                    onOpen: () => onOpenHelp?.(t.lockCalculatorModule?.helpTexts?.joistWidth),
                  }}
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      value={field.value}
                      decimalScale={1}
                      suffix={t.common.unitMm}
                      id={labelId}
                      isInvalid={isInvalid}
                      allowNegative={false}
                      disabled={readOnly}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />

            <Controller
              name="joistMember.height"
              control={control}
              rules={{ min: 0 }}
              render={({ field }) => (
                <FormField name="joistMember.height"
                  label={t.lockCalculatorModule?.timber?.joistHeight}
                  error={errors.joistMember?.height}
                  showHelp={{
                    tooltip: t.lockCalculatorModule?.helpTexts?.joistHeight,
                    onOpen: () => onOpenHelp?.(t.lockCalculatorModule?.helpTexts?.joistHeight),
                  }}
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      value={field.value}
                      decimalScale={1}
                      suffix={t.common.unitMm}
                      id={labelId}
                      isInvalid={isInvalid}
                      allowNegative={false}
                      disabled={readOnly}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />

            <Controller
              name="joistMember.alphaDeg"
              control={control}
              rules={{ min: -30, max: 45 }}
              render={({ field }) => (
                <FormField name="joistMember.alphaDeg"
                  label={t.lockCalculatorModule?.loads?.alphaDeg ?? t.lockCalculatorModule?.timber?.joistAngle}
                  error={errors.joistMember?.alphaDeg}
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      value={field.value}
                      decimalScale={1}
                      suffix={"°"}
                      id={labelId}
                      isInvalid={isInvalid}
                      allowNegative={true}
                      disabled={readOnly}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />

            <Controller
              name="joistMember.betaDeg"
              control={control}
              rules={{ min: -20, max: 20 }}
              render={({ field }) => (
                <FormField name="joistMember.betaDeg"
                  label={t.lockCalculatorModule?.timber?.joistAngleBeta}
                  error={errors.joistMember?.betaDeg}
                  isOptional
                  showHelp={{
                    tooltip: t.lockCalculatorModule?.helpTexts?.angleBeta,
                    onOpen: () => onOpenHelp?.(t.lockCalculatorModule?.helpTexts?.angleBeta),
                  }}
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      value={field.value}
                      decimalScale={1}
                      suffix={"°"}
                      id={labelId}
                      isInvalid={isInvalid}
                      allowNegative={true}
                      disabled={readOnly}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── i18n-aware option builders ───

export function buildHeaderOrientationOptions(t: ReturnType<typeof useI18n>["translation"]) {
  const labels = headerOrientationLabels(t);
  return [
    { value: "beam", label: labels.beam },
    { value: "column", label: labels.column },
  ];
}
